import React, { useState, useEffect } from 'react';
import { Layout, Row, Col, Typography, Space } from 'antd';
import { Link } from 'react-router-dom';
import { 
  PhoneOutlined, 
  MailOutlined, 
  EnvironmentOutlined,
  FacebookOutlined,
  XOutlined,
  InstagramOutlined
} from '@ant-design/icons';
import hspLogo from '../../../images/hsplogo.png';
import { theme } from '../../../styles/theme';
import { FOOTER_CONTENT } from '../../../constants/footerContent';

const { Footer: AntFooter } = Layout;
const { Text, Title } = Typography;

const Footer = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return (
    <AntFooter style={{ 
      background: theme.colors.primary, 
      color: theme.colors.white,
      padding: isMobile ? '40px 0 20px 0' : '60px 0 30px 0',
      fontFamily: theme.fonts.primary
    }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px' }}>
        <Row gutter={isMobile ? [16, 16] : [32, 32]}>
          {/* Company Info */}
          <Col xs={24} sm={12} md={6}>
            <div style={{ marginBottom: isMobile ? '0.5rem' : '1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: isMobile ? '0.5rem' : '1rem' }}>
                <img 
                  src={hspLogo} 
                  alt="HSP Logo" 
                  style={{ 
                    height: '40px', 
                    width: 'auto',
                    marginRight: '8px'
                  }} 
                />
                <Title level={4} style={{ 
                  color: theme.colors.white, 
                  margin: 0,
                  fontFamily: theme.fonts.primary,
                  fontWeight: 'bold'
                }}>
                  {FOOTER_CONTENT.company.name}
                </Title>
              </div>
              <Text style={{ 
                color: theme.colors.white, 
                fontSize: isMobile ? '0.9rem' : '1rem',
                fontFamily: theme.fonts.primary,
                opacity: 0.9
              }}>
                {FOOTER_CONTENT.company.tagline}
              </Text>
            </div>
          </Col>

          {/* Services */}
          <Col xs={24} sm={12} md={6}>
            <Title level={5} style={{ 
              color: theme.colors.white, 
              marginBottom: isMobile ? '0.5rem' : '1rem',
              fontFamily: theme.fonts.primary,
              fontWeight: 'bold'
            }}>
              {FOOTER_CONTENT.services.title}
            </Title>
            <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '0.25rem' : '0.5rem' }}>
              {FOOTER_CONTENT.services.items.map((item, index) => (
                <Link key={index} to={item.link} style={{ color: theme.colors.white, opacity: 0.9, textDecoration: 'none' }}>
                  <Text style={{ color: theme.colors.white, opacity: 0.9, fontFamily: theme.fonts.primary }}>{item.label}</Text>
                </Link>
              ))}
            </div>
          </Col>

          {/* Quick Links */}
          <Col xs={24} sm={12} md={6}>
            <Title level={5} style={{ 
              color: theme.colors.white, 
              marginBottom: isMobile ? '0.5rem' : '1rem',
              fontFamily: theme.fonts.primary,
              fontWeight: 'bold'
            }}>
              {FOOTER_CONTENT.quickLinks.title}
            </Title>
            <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '0.25rem' : '0.5rem' }}>
              {FOOTER_CONTENT.quickLinks.items.map((item, index) => (
                <Link key={index} to={item.link} style={{ color: theme.colors.white, opacity: 0.9, textDecoration: 'none' }}>
                  <Text style={{ color: theme.colors.white, opacity: 0.9, fontFamily: theme.fonts.primary }}>{item.label}</Text>
                </Link>
              ))}
            </div>
          </Col>

          {/* Contact Info */}
          <Col xs={24} sm={12} md={6}>
            <Title level={5} style={{ 
              color: theme.colors.white, 
              marginBottom: isMobile ? '0.5rem' : '1rem',
              fontFamily: theme.fonts.primary,
              fontWeight: 'bold'
            }}>
              {FOOTER_CONTENT.contact.title}
            </Title>
            <Space direction="vertical" size="small">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <PhoneOutlined style={{ color: theme.colors.white, opacity: 0.9 }} />
                <Text style={{ 
                  color: theme.colors.white, 
                  opacity: 0.9,
                  fontFamily: theme.fonts.primary
                }}>{FOOTER_CONTENT.contact.phone}</Text>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <MailOutlined style={{ color: theme.colors.white, opacity: 0.9 }} />
                <Text style={{ 
                  color: theme.colors.white, 
                  opacity: 0.9,
                  fontFamily: theme.fonts.primary
                }}>{FOOTER_CONTENT.contact.email}</Text>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <EnvironmentOutlined style={{ color: theme.colors.white, opacity: 0.9 }} />
                <Text style={{ 
                  color: theme.colors.white, 
                  opacity: 0.9,
                  fontFamily: theme.fonts.primary
                }}>{FOOTER_CONTENT.contact.location}</Text>
              </div>
            </Space>

            {/* Social Media */}
            <div style={{ marginTop: isMobile ? '1rem' : '1.5rem' }}>
              <Space size="middle">
                <a href={FOOTER_CONTENT.social.facebook} target="_blank" rel="noopener noreferrer">
                  <FacebookOutlined style={{ 
                    fontSize: '1.5rem', 
                    color: theme.colors.white, 
                    opacity: 0.9,
                    cursor: 'pointer',
                    transition: 'opacity 0.2s ease'
                  }} 
                  onMouseEnter={(e) => e.target.style.opacity = '1'}
                  onMouseLeave={(e) => e.target.style.opacity = '0.9'}
                  />
                </a>
                <a href={FOOTER_CONTENT.social.twitter} target="_blank" rel="noopener noreferrer">
                  <XOutlined style={{ 
                    fontSize: '1.5rem', 
                    color: theme.colors.white, 
                    opacity: 0.9,
                    cursor: 'pointer',
                    transition: 'opacity 0.2s ease'
                  }} 
                  onMouseEnter={(e) => e.target.style.opacity = '1'}
                  onMouseLeave={(e) => e.target.style.opacity = '0.9'}
                  />
                </a>
                <a href={FOOTER_CONTENT.social.instagram} target="_blank" rel="noopener noreferrer">
                  <InstagramOutlined style={{ 
                    fontSize: '1.5rem', 
                    color: theme.colors.white, 
                    opacity: 0.9,
                    cursor: 'pointer',
                    transition: 'opacity 0.2s ease'
                  }} 
                  onMouseEnter={(e) => e.target.style.opacity = '1'}
                  onMouseLeave={(e) => e.target.style.opacity = '0.9'}
                  />
                </a>
              </Space>
            </div>
          </Col>
        </Row>

        {/* Bottom Bar */}
        <div style={{ 
          borderTop: `1px solid ${theme.colors.white}`, 
          opacity: 0.3,
          marginTop: isMobile ? '1.5rem' : '2rem', 
          paddingTop: isMobile ? '1rem' : '1.5rem',
          textAlign: 'center'
        }}>
          <Row justify="space-between" align="middle" gutter={isMobile ? [0, 16] : [0, 0]}>
            <Col xs={24} sm={12}>
              <Text style={{ 
                color: theme.colors.white, 
                opacity: 0.9,
                fontFamily: theme.fonts.primary,
                textAlign: isMobile ? 'center' : 'left'
              }}>
                {FOOTER_CONTENT.legal.copyright}
              </Text>
            </Col>
            <Col xs={24} sm={12}>
              <Space size="large" style={{ justifyContent: isMobile ? 'center' : 'flex-end', width: '100%' }}>
                {FOOTER_CONTENT.legal.links.map((item, index) => (
                  <Link key={index} to={item.link} style={{ textDecoration: 'none' }}>
                    <Text style={{ 
                      color: theme.colors.white, 
                      opacity: 0.9,
                      cursor: 'pointer',
                      fontFamily: theme.fonts.primary,
                      transition: 'opacity 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.target.style.opacity = '1'}
                    onMouseLeave={(e) => e.target.style.opacity = '0.9'}
                    >{item.label}</Text>
                  </Link>
                ))}
              </Space>
            </Col>
          </Row>
        </div>
      </div>
    </AntFooter>
  );
};

export default Footer;
